import { notifySessionExpired } from "@/shared/auth/authSession";
import type { AuthRealm } from "@/shared/auth/authRealm";
import {
  isJwtExpiringSoon,
  readStoredTokens,
  refreshCognitoTokens,
  storeTokens,
  TOKEN_REFRESH_BUFFER_SECONDS,
  type CognitoTokens,
} from "@/shared/auth/cognito";
import { getCognitoRuntimeConfig } from "@/shared/auth/cognitoRuntimeConfig";

type EnsureValidBearerTokenOptions = {
  /** Bypass the expiry check and always call Cognito (e.g. after a 401). */
  forceRefresh?: boolean;
};

type AuthTokenSyncHandler = (tokens: CognitoTokens) => void;

const refreshInFlight = new Map<AuthRealm, Promise<string | null>>();
let syncHandler: AuthTokenSyncHandler | null = null;

export function registerAuthTokenSyncHandler(
  handler: AuthTokenSyncHandler | null,
): void {
  syncHandler = handler;
}

export function clearTokenRefreshState(): void {
  refreshInFlight.clear();
  syncHandler = null;
}

/** Clears in-flight refresh promises without unregistering the sync handler. */
export function clearTokenRefreshInFlight(): void {
  refreshInFlight.clear();
}

async function refreshIdTokenForRealm(
  realm: AuthRealm,
  refreshToken: string,
): Promise<string | null> {
  const config = getCognitoRuntimeConfig();
  if (!config?.clientId || !config.cognitoDomain) {
    notifySessionExpired();
    return null;
  }

  try {
    const tokens = await refreshCognitoTokens({
      cognitoDomain: config.cognitoDomain,
      clientId: config.clientId,
      refreshToken,
    });
    storeTokens(realm, tokens);
    syncHandler?.(tokens);
    return tokens.id_token;
  } catch {
    notifySessionExpired();
    return null;
  }
}

/**
 * Returns a valid Cognito ID token for API calls, refreshing via the stored
 * refresh token when the current ID token is expired or expiring soon.
 */
export async function ensureValidBearerToken(
  realm: AuthRealm,
  options: EnsureValidBearerTokenOptions = {},
): Promise<string | null> {
  const stored = readStoredTokens(realm);
  if (!stored?.refreshToken) {
    return null;
  }

  if (
    !options.forceRefresh &&
    stored.idToken &&
    !isJwtExpiringSoon(stored.idToken, TOKEN_REFRESH_BUFFER_SECONDS)
  ) {
    return stored.idToken;
  }

  const inFlight = refreshInFlight.get(realm);
  if (inFlight) {
    return inFlight;
  }

  const promise = refreshIdTokenForRealm(realm, stored.refreshToken).finally(() => {
    refreshInFlight.delete(realm);
  });
  refreshInFlight.set(realm, promise);
  return promise;
}
